import React from "react";
import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import "./smsTerms.scss";

const EFFECTIVE_DATE = "October 20, 2026";

const SmsTerms = () => {
    return (
        <div className="sms-terms-page">
            <div className="sms-terms-page-header">
                <Header />
            </div>

            <div className="sms-terms-page-container">
                <h1 className="sms-terms-title">
                    <ChatOutlinedIcon />&nbsp;&nbsp;SMS Terms &amp; Conditions
                </h1>
                <p className="sms-terms-effective">
                    <strong>Effective date:</strong> {EFFECTIVE_DATE}
                </p>

                <section className="sms-terms-section">
                    <p>
                        These SMS Terms &amp; Conditions (&ldquo;SMS Terms&rdquo;) govern the
                        text messaging program operated by VirtualPsych (&ldquo;VirtualPsych,&rdquo;
                        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By providing
                        your mobile phone number and opting in to receive text messages from
                        us, you agree to these SMS Terms and to our{" "}
                        <a href="/privacy" className="sms-terms-link">Privacy Policy</a>.
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>1. Program Sponsor (Brand)</h2>
                    <p>
                        This SMS program is operated by <strong>VirtualPsych</strong>, located
                        at 1715 Indian Wood Circle, Maumee, OH 43537. VirtualPsych is the
                        brand responsible for all messages sent under this program.
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>2. Types of Messages and Purpose</h2>
                    <p>
                        VirtualPsych sends transactional, service-related text messages to
                        patients and prospective patients who have provided their mobile phone
                        number and consented to receive messages. The purpose of the program
                        is to support your care with us. Messages may include:
                    </p>
                    <ul>
                        <li>Appointment confirmations;</li>
                        <li>Appointment reminders;</li>
                        <li>Rescheduling, cancellation, and wait-list notifications;</li>
                        <li>
                            Replies to questions you send us about your visit, intake, or
                            scheduling;
                        </li>
                        <li>
                            Pre-visit instructions and links needed to join or prepare for a
                            telehealth appointment; and
                        </li>
                        <li>
                            Account- and billing-related notices directly related to your
                            care.
                        </li>
                    </ul>
                    <p>
                        <strong>
                            We do not send marketing or promotional text messages through this
                            program.
                        </strong>
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>3. How to Opt In</h2>
                    <p>
                        You may opt in to receive text messages by providing your mobile phone
                        number and granting consent through our website forms, in person at
                        our office, over the phone, or in writing. You are not required to
                        consent to receive text messages as a condition of receiving care.
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>4. Opt-Out (STOP) and Help (HELP)</h2>
                    <p>
                        You can cancel the SMS service at any time. Reply{" "}
                        <strong>STOP</strong> to any message you receive from us, and we will
                        stop sending you text messages. You will receive a single confirmation
                        message acknowledging that you have been unsubscribed, after which no
                        additional messages will be sent. If you want to resume messages, you
                        must opt in again.
                    </p>
                    <p>
                        If you need help or have questions about the SMS program, reply{" "}
                        <strong>HELP</strong> to any message, call us at{" "}
                        <a href="tel:+18778394824" className="sms-terms-link">(877) 839-4824</a>,
                        or email{" "}
                        <a href="mailto:support@virtualpsych.com" className="sms-terms-link">
                            support@virtualpsych.com
                        </a>
                        .
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>5. Message Frequency</h2>
                    <p>
                        <strong>Message frequency varies</strong> based on your interactions
                        with us and your scheduled appointments. On average, you can expect
                        approximately 2&ndash;6 messages per month, with additional messages
                        around the time of a scheduled appointment (for example,
                        confirmations, reminders, and reply messages).
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>6. Message and Data Rates</h2>
                    <p>
                        <strong>Message and data rates may apply.</strong> Standard message
                        and data rates charged by your mobile carrier will apply to each
                        message sent or received. Please contact your mobile carrier for
                        information about your specific messaging plan. VirtualPsych is not
                        responsible for any charges imposed by your mobile carrier.
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>7. Supported Carriers</h2>
                    <p>
                        The program is compatible with major U.S. carriers, including
                        AT&amp;T, T-Mobile, Verizon Wireless, Sprint, Boost, U.S. Cellular,
                        MetroPCS, and others. Carriers are not liable for delayed or
                        undelivered messages. Delivery of messages is subject to the effective
                        transmission from your wireless service provider and is outside of our
                        control.
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>8. Privacy</h2>
                    <p>
                        Information collected in connection with the SMS program is handled in
                        accordance with our{" "}
                        <a href="/privacy" className="sms-terms-link">Privacy Policy</a>.{" "}
                        <strong>
                            No mobile information will be shared with third parties or
                            affiliates for marketing or promotional purposes. Text messaging
                            opt-in data and consent will not be shared with or sold to any
                            third parties.
                        </strong>{" "}
                        Information may be shared only with service providers that help us
                        deliver messages (such as our messaging and telecommunications
                        providers), and only for that purpose.
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>9. Eligibility</h2>
                    <p>
                        You must be at least 18 years old (or the age of majority in your
                        state) and the account holder or authorized user of the mobile phone
                        number you provide. By opting in, you represent that you meet these
                        requirements and that you consent to receive text messages at the
                        number you provided.
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>10. Not for Emergencies</h2>
                    <p>
                        The SMS program is not monitored 24/7 and is not intended for medical
                        emergencies or urgent clinical issues. If you are experiencing a
                        medical or psychiatric emergency, call 911 or go to the nearest
                        emergency room. If you are in crisis, you can also call or text{" "}
                        <strong>988</strong> to reach the Suicide &amp; Crisis Lifeline.
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>11. Changes to These SMS Terms</h2>
                    <p>
                        We may update these SMS Terms from time to time. When we do, we will
                        update the &ldquo;Effective date&rdquo; above and post the revised
                        terms on this page. Your continued participation in the SMS program
                        after any change takes effect means you accept the updated terms.
                    </p>
                </section>

                <section className="sms-terms-section">
                    <h2>12. Contact Us</h2>
                    <p>
                        If you have questions about these SMS Terms or the SMS program, please
                        contact us at:
                    </p>
                    <p>
                        <strong>VirtualPsych</strong>
                        <br />
                        1715 Indian Wood Circle, Maumee, OH 43537
                        <br />
                        Phone:{" "}
                        <a href="tel:+18778394824" className="sms-terms-link">(877) 839-4824</a>
                        <br />
                        Email:{" "}
                        <a href="mailto:support@virtualpsych.com" className="sms-terms-link">
                            support@virtualpsych.com
                        </a>
                    </p>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default SmsTerms;
